import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const AuthConfirmPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const tokenHash = searchParams.get('token_hash');
  const type = searchParams.get('type') || 'recovery';
  const nextPath = searchParams.get('next') || '/reset-password';

  const isRecovery = type === 'recovery';

  const pageTitle = useMemo(() => {
    if (isRecovery) return 'Confirm Password Reset';
    return 'Confirm Sign In';
  }, [isRecovery]);

  const handleConfirm = async () => {
    if (!tokenHash) {
      setError('This link is missing a verification token. Please request a new email.');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type,
      });

      if (verifyError) {
        setError(
          verifyError.message ||
            'This link is invalid or has expired. Please request a new password reset email.'
        );
        return;
      }

      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err?.message || 'Unable to verify this link. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  if (!tokenHash) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 card-shadow text-center space-y-4">
          <Icon name="AlertTriangle" size={40} className="text-warning mx-auto" />
          <h1 className="text-xl font-semibold text-foreground">Invalid Link</h1>
          <p className="text-sm text-muted-foreground">
            This confirmation link is incomplete. Request a new password reset email and use the
            latest link only once.
          </p>
          <Button variant="default" fullWidth onClick={() => navigate('/forgot-password')}>
            Request New Reset Link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 card-shadow">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name={isRecovery ? 'KeyRound' : 'Mail'} size={32} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">{pageTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {isRecovery
              ? 'Click the button below to verify your password reset request. This extra step keeps your account secure if an email scanner accessed the link first.'
              : 'Click the button below to complete sign-in.'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <Button
            variant="default"
            size="lg"
            fullWidth
            iconName={isRecovery ? 'KeyRound' : 'LogIn'}
            loading={isVerifying}
            disabled={isVerifying}
            onClick={handleConfirm}
          >
            {isRecovery ? 'Continue to Reset Password' : 'Continue'}
          </Button>

          {isRecovery && (
            <Button
              variant="outline"
              fullWidth
              onClick={() => navigate('/forgot-password')}
              disabled={isVerifying}
            >
              Request a New Link
            </Button>
          )}

          <div className="text-center pt-2">
            <Link to="/login" className="text-sm text-primary hover:text-primary/80">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthConfirmPage;
